import { ArrowUpRight } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { BRIEF_FORM_URL } from "../lib/contact";

const services = [
  {
    id: "redes",
    title: "Gestión de Redes Sociales",
    pre: "Gestión de ",
    italic: "Redes",
    post: " Sociales",
    desc: "Contenido estratégico, calendario editorial y community management para que tu marca crezca con coherencia.",
    bullets: ["Planeación mensual", "Reels & carruseles", "Community Management"],
    image:
      "https://static.prod-images.emergentagent.com/jobs/820546bd-8e54-4159-a820-2bb5698b90fc/images/9cdd4d5585bc90c79ba074c2be7414f9dcc542ae4744efed5a5dd9e2a8f674a8.png",
    num: "01",
  },
  {
    id: "video",
    title: "Fotografía y Video Profesional",
    pre: "Fotografía y ",
    italic: "Video",
    post: " Profesional",
    desc: "Producción audiovisual de alta calidad. Capturamos tu marca con dirección creativa cinematográfica.",
    bullets: ["Sesiones de producto", "Video institucional", "Reels & ads"],
    image:
      "https://images.pexels.com/photos/2873486/pexels-photo-2873486.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    num: "02",
  },
  {
    id: "branding",
    title: "Branding e Identidad Visual",
    pre: "Branding e ",
    italic: "Identidad",
    post: " Visual",
    desc: "Logos, sistemas visuales y manuales de marca pensados para durar y diferenciarte de la competencia.",
    bullets: ["Logo & sistema", "Manual de marca", "Papelería digital"],
    image:
      "https://static.prod-images.emergentagent.com/jobs/820546bd-8e54-4159-a820-2bb5698b90fc/images/19d33645bec616ba0a11e1fdf57814fb7c621cc5a78e9a813a74682d656efa8e.png",
    num: "03",
  },
  {
    id: "ads",
    title: "Marketing Digital y Ads",
    pre: "Marketing ",
    italic: "Digital",
    post: " y Ads",
    desc: "Campañas pagadas en Meta y Google con foco en conversiones. Datos, optimización y resultados medibles.",
    bullets: ["Meta Ads", "Google Ads", "Reportes claros"],
    image:
      "https://static.prod-images.emergentagent.com/jobs/820546bd-8e54-4159-a820-2bb5698b90fc/images/9978e0d551892b846314dadd538353a2d1a370e1c1ae5a5150b0358abedb6848.png",
    num: "04",
  },
];

export const Services = () => {
  const ref = useReveal();
  return (
    <section
      id="que-hacemos"
      data-testid="services-section"
      className="relative py-24 md:py-36 bg-[#e5e3dc]/60"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div ref={ref} className="reveal flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-[#ca0013]" />
              <span className="font-label text-xs text-[#ca0013]">
                Qué hacemos
              </span>
            </div>
            <h2
              data-testid="services-title"
              className="font-display text-4xl md:text-6xl tracking-tighter leading-[0.95]"
            >
              Servicios diseñados
              <br />
              para{" "}
              <span className="font-editorial text-[#ca0013] font-normal">
                escalar
              </span>{" "}
              tu marca.
            </h2>
          </div>
          <p className="font-body text-base text-[#4a4a4a] max-w-sm">
            Cuatro líneas de trabajo, un solo objetivo: convertir tu presencia
            digital en un activo que vende.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {services.map((s, idx) => (
            <a
              key={s.id}
              href={BRIEF_FORM_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`service-card-${s.id}`}
              aria-label={`Llenar brief para ${s.title}`}
              className="service-tile group relative bg-white border border-black/10 overflow-hidden block cursor-pointer hover:border-[#ca0013] focus:outline-none focus:ring-2 focus:ring-[#ca0013] focus:ring-offset-2 focus:ring-offset-[#e5e3dc]"
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#eeede9]">
                <img
                  src={s.image}
                  alt={s.title}
                  className="tile-img w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute top-4 left-4 font-editorial text-2xl text-white drop-shadow-lg">
                  {s.num}
                </div>
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/95 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight size={18} className="text-[#ca0013]" />
                </div>
              </div>

              <div className="p-7 md:p-9">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight leading-tight">
                    {s.pre}
                    <span className="font-editorial text-[#ca0013] font-normal">
                      {s.italic}
                    </span>
                    {s.post}
                  </h3>
                </div>
                <p className="font-body text-[#4a4a4a] mt-3 leading-relaxed">
                  {s.desc}
                </p>
                <ul className="mt-6 space-y-2">
                  {s.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="font-body text-sm text-[#111] flex items-center gap-3"
                    >
                      <span className="w-1.5 h-1.5 bg-[#ca0013] rounded-full" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-7 pt-5 border-t border-black/10 flex items-center justify-between">
                  <span className="font-label text-xs text-[#111] group-hover:text-[#ca0013] transition-colors">
                    Llenar brief
                  </span>
                  <ArrowUpRight
                    size={20}
                    className="text-[#111] group-hover:text-[#ca0013] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                  />
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
